import React, { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import Topnavbar from "../dashboard/topnavbar";
import Sidennav from "../dashboard/sidenavbar";
import Swal from "sweetalert2";

const FaceKiosk = () => {
  const accessstaffid = localStorage.getItem("staffid");
  const accesstoken = localStorage.getItem("token");

  const [isAdmin, setIsAdmin] = useState(null); // null=loading
  const [branchtype, setBranchType] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [cameraActive, setCameraActive] = useState(false);
  const [autoScan, setAutoScan] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastPunch, setLastPunch] = useState(null); // { name, punchType }

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const autoScanRef = useRef(null);
  const scanningRef = useRef(false);

  // Admin access check
  useEffect(() => {
    if (!accessstaffid) { setIsAdmin(false); return; }
    axios
      .get(`${baseurladdress}face-kiosk-access?staffid=${accessstaffid}`, {
        headers: { Authorization: `Bearer ${accesstoken}` },
      })
      .then(res => setIsAdmin(res.data.isAdmin))
      .catch(() => setIsAdmin(false));
  }, []);

  // Load branches and auto-select admin's assigned branch
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchRes = await axios.get(`${baseurladdress}getallbranchestype`, {
          headers: { Authorization: `Bearer ${accesstoken}` },
        });
        setBranchType(branchRes.data.response || []);

        const empBranchRes = await axios.get(`${baseurladdress}getemployeebranchdetails/${accessstaffid}`, {
          headers: { Authorization: `Bearer ${accesstoken}` },
        });
        if (empBranchRes.data.response) {
          setSelectedBranch(empBranchRes.data.response.branch_id.toString());
        }
      } catch {}
    };
    fetchBranches();
  }, []);

  // Camera helpers
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
      setAutoScan(true); // auto scan starts immediately when camera opens
    } catch {
      Swal.fire({ icon: "error", title: "Camera Error", text: "Unable to access camera. Please allow camera permission." });
    }
  }, []);

  const stopCamera = useCallback(() => {
    clearInterval(autoScanRef.current);
    setAutoScan(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth || 640;
    c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.9);
  }, []);

  // Auto scan loop
  useEffect(() => {
    if (autoScan && cameraActive) {
      autoScanRef.current = setInterval(() => {
        handleIdentify(null, true);
      }, 2500);
    } else {
      clearInterval(autoScanRef.current);
    }
    return () => clearInterval(autoScanRef.current);
  }, [autoScan, cameraActive, selectedBranch]);

  // Kiosk punch — called after admin confirms popup
  const kioskPunch = async (staffid, branchId, shiftId, forcePunch) => {
    try {
      const res = await axios.post(
        `${baseurladdress}face-kiosk-punch`,
        { staffid, branch_id: branchId, shift_id: shiftId, date: selectedDate, force_punch: forcePunch },
        { headers: { Authorization: `Bearer ${accesstoken}` } }
      );
      if (res.data.success) {
        setLastPunch({ name: res.data.empName, punchType: res.data.punchType });
        setTimeout(() => setLastPunch(null), 5000);
        Swal.fire({
          icon: "success",
          title: res.data.punchType === "in" ? "Punched In!" : "Punched Out!",
          text: res.data.empName,
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: "Punch Failed", text: res.data.message });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Server error." });
    }
  };

  // Shift card picker — same design as punchly
  const showShiftCardPicker = async (title, shifts) => {
    const shiftColors = {
      "Day Shift": "#FFDCE5", "Morning Shift": "#DCFCE7", "Evening Shift": "#D6EFFF",
      "Night Shift": "#E9D8FD", "Afternoon Shift": "#FFF1C2", "Fixed Shift": "#D9F8F0",
    };
    function fmtTime(t) {
      const [h, m] = t.split(":");
      let hr = parseInt(h, 10);
      const ap = hr >= 12 ? "PM" : "AM";
      hr = hr % 12 || 12;
      return `${hr}:${m} ${ap}`;
    }
    let cardHtml = `<div class="fp-shift-container">`;
    (shifts || []).forEach(shift => {
      const bg = shiftColors[shift.type_name?.trim()] || "#F5F5F5";
      cardHtml += `
        <label class="fp-shift-card" style="background:${bg};">
          <input type="radio" name="fkSelectedShift" value="${shift.type_id}">
          <div class="fp-shift-content">
            <div class="fp-shift-icon">🕒</div>
            <div class="fp-shift-details">
              <div class="fp-shift-name">${shift.type_name}</div>
              <div class="fp-shift-time">${fmtTime(shift.shift_from)} - ${fmtTime(shift.shift_to)}</div>
            </div>
          </div>
        </label>`;
    });
    cardHtml += `</div>`;
    const result = await Swal.fire({
      title, html: cardHtml, width: "720px",
      showCancelButton: true, confirmButtonText: "Continue", cancelButtonText: "Cancel",
      customClass: { popup: "fp-shift-popup" }, allowOutsideClick: false,
      didOpen: () => {
        const s = document.createElement("style");
        s.innerHTML = `
          .fp-shift-container{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:18px;margin-top:20px;}
          .fp-shift-card{display:block;cursor:pointer;border-radius:16px;padding:18px;transition:.3s;position:relative;border:2px solid transparent;}
          .fp-shift-card:hover{transform:translateY(-4px);box-shadow:0 10px 25px rgba(0,0,0,.15);}
          .fp-shift-card input{position:absolute;right:15px;top:15px;transform:scale(1.3);}
          .fp-shift-content{display:flex;align-items:center;gap:15px;}
          .fp-shift-icon{width:55px;height:55px;border-radius:50%;background:white;display:flex;justify-content:center;align-items:center;font-size:26px;box-shadow:0 4px 10px rgba(0,0,0,.1);flex-shrink:0;}
          .fp-shift-name{font-size:20px;font-weight:700;color:#222;}
          .fp-shift-time{margin-top:6px;font-size:15px;color:#555;}
          .fp-shift-card:has(input:checked){border:2px solid #4F46E5;box-shadow:0 0 15px rgba(79,70,229,.35);}
          .fp-shift-popup{border-radius:20px!important;}
          .fp-shift-popup .swal2-title{font-size:30px!important;font-weight:700!important;}
          .fp-shift-popup .swal2-confirm{background:#4F46E5!important;border-radius:10px!important;padding:10px 28px!important;}
          .fp-shift-popup .swal2-cancel{border-radius:10px!important;padding:10px 28px!important;}
        `;
        document.head.appendChild(s);
      },
      preConfirm: () => {
        const sel = document.querySelector('input[name="fkSelectedShift"]:checked');
        if (!sel) { Swal.showValidationMessage("Please select a shift"); return false; }
        return sel.value;
      },
    });
    return result.isConfirmed ? result.value : null;
  };

  // Inject fk- popup styles once
  const injectFkStyles = () => {
    const s = document.createElement("style");
    s.innerHTML = `
      .fk-popup-wrap{border-radius:20px;padding:25px;}
      .fk-card{text-align:center;padding:8px 0;}
      .fk-photo{width:90px;height:90px;border-radius:50%;object-fit:cover;border:3px solid #EEF2FF;box-shadow:0 4px 12px rgba(0,0,0,.15);margin-bottom:12px;}
      .fk-name{font-size:26px;font-weight:700;color:#111;margin-bottom:10px;}
      .fk-punch-badge{display:inline-block;padding:7px 22px;border-radius:20px;font-size:15px;font-weight:700;margin-bottom:14px;}
      .fk-badge-in{background:#DCFCE7;color:#16A34A;}
      .fk-badge-out{background:#FEE2E2;color:#DC2626;}
      .fk-shift-pill{display:inline-block;background:#EEF2FF;color:#4F46E5;padding:8px 22px;border-radius:24px;font-size:18px;font-weight:700;margin-bottom:6px;}
      .fk-shift-time{font-size:14px;color:#666;}
      .fk-popup-wrap .swal2-confirm{background:#4F46E5!important;border-radius:10px!important;padding:10px 30px!important;font-size:16px!important;}
      .fk-popup-wrap .swal2-cancel{background:#6B7280!important;border-radius:10px!important;padding:10px 30px!important;font-size:16px!important;}
      .fp-assigned-popup-wrap{border-radius:20px;padding:25px;}
      .fp-assigned-popup{text-align:center;}
      .fk-assigned-photo{width:80px;height:80px;border-radius:50%;object-fit:cover;border:3px solid #EEF2FF;box-shadow:0 4px 12px rgba(0,0,0,.12);margin-bottom:10px;}
      .fp-assigned-title{font-size:24px;font-weight:700;color:#222;}
      .fp-assigned-name{display:inline-block;background:#EEF2FF;color:#4F46E5;padding:10px 28px;border-radius:30px;font-size:21px;font-weight:700;}
      .fp-assigned-time{margin-top:14px;font-size:16px;color:#666;font-weight:600;}
      .fp-assigned-message{margin-top:16px;font-size:14px;color:#555;line-height:1.6;}
      .fp-assigned-popup-wrap .swal2-confirm{background:#4F46E5!important;border-radius:10px!important;padding:10px 30px!important;font-size:16px!important;}
      .fp-assigned-popup-wrap .swal2-cancel{background:#6B7280!important;border-radius:10px!important;padding:10px 30px!important;font-size:16px!important;}
      .fp-apr-popup-wrap{border-radius:20px;padding:25px;}
      .fp-apr-popup{text-align:center;}
      .fp-apr-icon{width:90px;height:90px;margin:auto;border-radius:50%;background:#FEF3C7;font-size:38px;display:flex;justify-content:center;align-items:center;box-shadow:0 5px 15px rgba(245,158,11,.2);}
      .fp-apr-title{margin-top:18px;font-size:26px;font-weight:700;color:#222;}
      .fp-apr-card{margin:18px auto 0;background:#F9FAFB;border-radius:12px;padding:14px 18px;max-width:320px;text-align:left;}
      .fp-apr-row{display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid #E5E7EB;}
      .fp-apr-row:last-child{border-bottom:none;}
      .fp-apr-label{font-size:13px;color:#6B7280;font-weight:500;}
      .fp-apr-val{font-size:14px;font-weight:700;color:#111;}
      .fp-apr-diff{color:#DC2626;}
      .fp-apr-message{margin-top:18px;font-size:14px;color:#555;line-height:1.6;}
      .fp-apr-popup-wrap .swal2-confirm{background:#4F46E5!important;border-radius:10px!important;padding:10px 30px!important;font-size:16px!important;}
      .fp-apr-popup-wrap .swal2-cancel{background:#6B7280!important;border-radius:10px!important;padding:10px 30px!important;font-size:16px!important;}
    `;
    document.head.appendChild(s);
  };

  // Main identification + popup handler
  const handleIdentify = async (storedImage = null, isAutoScan = false) => {
    if (scanningRef.current) return;
    if (!selectedBranch) {
      if (!isAutoScan) Swal.fire({ icon: "warning", title: "Select a Branch", text: "Please select a branch before scanning." });
      return;
    }

    const imageBase64 = storedImage || captureFrame();
    if (!imageBase64) return;

    scanningRef.current = true;
    setScanning(true);

    try {
      const res = await axios.post(
        `${baseurladdress}face-identify`,
        { branch_id: selectedBranch, imageBase64, date: selectedDate },
        { headers: { Authorization: `Bearer ${accesstoken}` } }
      );
      const data = res.data;

      // Silent fail for auto-scan on no-face / no-match
      if (!data.success || data.no_match || data.face_error) {
        if (!isAutoScan) {
          Swal.fire({
            icon: data.face_error ? "error" : "warning",
            title: data.face_error ? "Camera Issue" : "Face Not Recognized",
            text: data.message || "Please look directly at the camera and try again.",
            timer: 2500, showConfirmButton: false,
          });
        }
        return;
      }

      // Identified — stop auto scan and show popup
      clearInterval(autoScanRef.current);
      setAutoScan(false);
      scanningRef.current = false;
      setScanning(false);

      const photoUrl = `${baseurladdress}face-photos/${data.emp_id}.jpg`;
      const scoreText = `<p style="color:green;font-weight:bold;margin-bottom:8px;">✓ ${data.emp_name} recognized (${(data.similarity_score * 100).toFixed(1)}% match)</p>`;

      function fmtTime(t) {
        if (!t) return "";
        const [h, m] = t.split(":");
        let hr = parseInt(h, 10);
        const ap = hr >= 12 ? "PM" : "AM";
        hr = hr % 12 || 12;
        return `${hr}:${m} ${ap}`;
      }

      // ── "identified" — clean case: home branch, assigned shift, no conflicts ──
      if (data.success === "identified") {
        const as = data.assigned_shift;
        const shiftBlock = as
          ? `<div class="fk-shift-pill">${as.type_name}</div><div class="fk-shift-time">${fmtTime(as.shift_from)} – ${fmtTime(as.shift_to)}</div>`
          : "";
        const punchBadge = data.punch_type === "in"
          ? `<div class="fk-punch-badge fk-badge-in">⬆ Punch IN</div>`
          : `<div class="fk-punch-badge fk-badge-out">⬇ Punch OUT</div>`;

        const confirm = await Swal.fire({
          title: "",
          html: `
            ${scoreText}
            <div class="fk-card">
              <img src="${photoUrl}" class="fk-photo" onerror="this.style.display='none'" alt="" />
              <div class="fk-name">${data.emp_name}</div>
              ${punchBadge}
              ${shiftBlock}
            </div>
          `,
          showCancelButton: true, confirmButtonText: "Confirm", cancelButtonText: "Cancel",
          width: "420px", customClass: { popup: "fk-popup-wrap" }, allowOutsideClick: false,
          didOpen: injectFkStyles,
        });
        if (confirm.isConfirmed) {
          await kioskPunch(data.emp_id, selectedBranch, as?.type_id || null, 0);
        }
        return;
      }

      // ── "assigned_shift_confirmation" — multiple overlapping shifts ──
      if (data.success === "assigned_shift_confirmation") {
        const as = data.assigned_shift;
        const step1 = await Swal.fire({
          title: "",
          html: `
            ${scoreText}
            <div class="fp-assigned-popup">
              <img src="${photoUrl}" class="fk-assigned-photo" onerror="this.style.display='none'" alt="" />
              <div class="fp-assigned-title">${data.emp_name}</div>
              <div class="fp-assigned-name" style="margin-top:12px;">${as.type_name}</div>
              <div class="fp-assigned-time">${fmtTime(as.shift_from)} – ${fmtTime(as.shift_to)}</div>
              <div class="fp-assigned-message">Assigned shift for this staff.<br>Continue or choose a different shift?</div>
            </div>
          `,
          showCancelButton: true, confirmButtonText: "Continue", cancelButtonText: "Other Shift",
          width: "500px", customClass: { popup: "fp-assigned-popup-wrap" }, allowOutsideClick: false,
          didOpen: injectFkStyles,
        });
        if (step1.isConfirmed) {
          await kioskPunch(data.emp_id, selectedBranch, as.type_id, 0);
        } else if (step1.dismiss === Swal.DismissReason.cancel) {
          const pickedId = await showShiftCardPicker("Multiple Shift Required", data.overlapping_shifts || []);
          if (pickedId) await kioskPunch(data.emp_id, selectedBranch, pickedId, 1);
        }
        return;
      }

      // ── "branch_and_shift_confirmation" — different branch + different shift ──
      if (data.success === "branch_and_shift_confirmation") {
        const warn = await Swal.fire({
          title: "Branch & Shift Changed",
          html: `<div style="text-align:center;">${scoreText}<p><strong>${data.emp_name}</strong> is assigned to a different branch and shift.</p><p style="color:red;font-weight:bold;margin-top:8px;">Admin approval will be required.</p></div>`,
          icon: "warning", showCancelButton: true, confirmButtonText: "Choose Shift", cancelButtonText: "Cancel", allowOutsideClick: false,
        });
        if (warn.isConfirmed) {
          const pickedId = await showShiftCardPicker("Select Shift", data.all_shifts || []);
          if (pickedId) await kioskPunch(data.emp_id, selectedBranch, pickedId, 1);
        }
        return;
      }

      // ── "branch_confirmation" — staff from a different branch ──
      if (data.success === "branch_confirmation") {
        const warn = await Swal.fire({
          title: "Different Branch",
          html: `<div style="text-align:center;">${scoreText}<p><strong>${data.emp_name}</strong> is assigned to a different branch.</p><p style="color:red;font-weight:bold;margin-top:8px;">Admin approval will be required.</p></div>`,
          icon: "warning", showCancelButton: true, confirmButtonText: "Choose Shift", cancelButtonText: "Cancel", allowOutsideClick: false,
        });
        if (warn.isConfirmed) {
          const pickedId = await showShiftCardPicker("Select Shift", data.all_shifts || []);
          if (pickedId) await kioskPunch(data.emp_id, selectedBranch, pickedId, 1);
        }
        return;
      }

      // ── "approval_required" — no shift assigned or shift mismatch ──
      if (data.success === "approval_required") {
        const assignedLine = data.assigned_shift_name
          ? `<div class="fp-apr-row"><span class="fp-apr-label">Assigned shift</span><span class="fp-apr-val">${data.assigned_shift_name}</span></div>`
          : "";
        const selectedLine = data.selected_shift_name
          ? `<div class="fp-apr-row"><span class="fp-apr-label">Punching for</span><span class="fp-apr-val fp-apr-diff">${data.selected_shift_name}</span></div>`
          : `<div class="fp-apr-row"><span class="fp-apr-label">Shift</span><span class="fp-apr-val fp-apr-diff">Not assigned</span></div>`;

        const confirmApr = await Swal.fire({
          title: "",
          html: `
            ${scoreText}
            <div class="fp-apr-popup">
              <div class="fp-apr-icon">⚠️</div>
              <div class="fp-apr-title">Admin Approval Required</div>
              <div class="fp-apr-card">${assignedLine}${selectedLine}</div>
              <div class="fp-apr-message">Attendance for <strong>${data.emp_name}</strong> will be sent for admin approval.<br>Do you want to continue?</div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: data.all_shifts ? "Choose Shift & Continue" : "Continue",
          cancelButtonText: "Cancel",
          width: "480px", customClass: { popup: "fp-apr-popup-wrap" }, allowOutsideClick: false,
          didOpen: injectFkStyles,
        });
        if (confirmApr.isConfirmed) {
          if (data.all_shifts) {
            const pickedId = await showShiftCardPicker("Select Shift", data.all_shifts);
            if (pickedId) await kioskPunch(data.emp_id, selectedBranch, pickedId, 1);
          } else {
            await kioskPunch(data.emp_id, selectedBranch, null, 1);
          }
        }
        return;
      }

    } catch (err) {
      if (!isAutoScan) {
        Swal.fire({ icon: "error", title: "Server Error", text: err.response?.data?.message || "Unable to process request." });
      }
    } finally {
      scanningRef.current = false;
      setScanning(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  if (isAdmin === null) {
    return (
      <>
        <Topnavbar /><Sidennav />
        <div className="content-wrap">
          <div className="main"><div className="container-fluid">
            <div style={{ textAlign: "center", padding: "80px 0", color: "#666" }}>Checking access...</div>
          </div></div>
        </div>
      </>
    );
  }

  if (isAdmin === false) {
    return (
      <>
        <Topnavbar /><Sidennav />
        <div className="content-wrap">
          <div className="main"><div className="container-fluid">
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div style={{ fontSize: "60px", marginBottom: "16px" }}>🔒</div>
              <h4 style={{ color: "#DC2626", fontWeight: 700 }}>Access Denied</h4>
              <p style={{ color: "#666" }}>Face Kiosk is only available to admin accounts.</p>
            </div>
          </div></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topnavbar />
      <Sidennav />

      <style>{`
        @keyframes scanLine {
          0%   { top: 0%; }
          100% { top: 100%; }
        }
        .swal2-container { z-index: 99999 !important; }
        .content-wrap { overflow-x: hidden; }
        .fk-page-wrap { max-width: 580px; margin: 0 auto; padding: 20px 16px; box-sizing: border-box; }
        .fk-header { background: linear-gradient(135deg,#4F46E5,#7C3AED); border-radius: 14px; padding: 16px 22px; color: white; margin-bottom: 16px; }
        .fk-header h4 { margin: 0; font-size: 20px; font-weight: 700; }
        .fk-header p { margin: 3px 0 0; opacity: .8; font-size: 13px; }
        .fk-controls { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 14px; }
        .fk-branch-select { flex: 1; min-width: 180px; padding: 8px 12px; border-radius: 10px; border: 1.5px solid #E5E7EB; font-size: 14px; }
        .fk-camera-box { position: relative; border-radius: 12px; overflow: hidden; border: 2px solid ${cameraActive && autoScan ? "#4F46E5" : "#E5E7EB"}; background: #111; transition: border-color .3s; min-height: 180px; }
        .fk-video { width: 100%; max-height: 340px; object-fit: cover; display: block; transform: scaleX(-1); }
        .fk-scan-line { position: absolute; left: 0; right: 0; height: 3px; background: linear-gradient(90deg,transparent,#4F46E5,transparent); animation: scanLine 1.8s linear infinite; pointer-events: none; }
        .fk-status { margin-top: 8px; text-align: center; font-size: 13px; font-weight: 600; color: ${autoScan ? "#4F46E5" : "#6B7280"}; }
        .fk-btn-row { margin-top: 12px; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
        .fk-last-punch { margin-top: 14px; border-radius: 10px; padding: 10px 16px; text-align: center; animation: fadeIn .3s ease; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
        .fk-last-punch.in-mode { background: #F0FDF4; border: 1px solid #BBF7D0; }
        .fk-last-punch.out-mode { background: #FFF1F2; border: 1px solid #FECDD3; }
        .fk-last-label { font-size: 11px; color: #6B7280; text-transform: uppercase; letter-spacing: .05em; }
        .fk-last-name { font-size: 16px; font-weight: 700; color: #111; margin-top: 2px; }
        .fk-last-badge { font-size: 12px; font-weight: 600; margin-top: 2px; color: ${lastPunch?.punchType === "in" ? "#16A34A" : "#DC2626"}; }
      `}</style>

      <div className="content-wrap">
        <div className="main">
          <div className="container-fluid">
            <div className="fk-page-wrap">

              {/* Header */}
              <div className="fk-header">
                <h4>📷 Face Kiosk</h4>
                <p>Staff walk up and face the camera — system identifies and punches automatically.</p>
              </div>

              {/* Branch + Date controls */}
              <div className="fk-controls">
                <select
                  className="fk-branch-select"
                  value={selectedBranch}
                  onChange={e => setSelectedBranch(e.target.value)}
                >
                  <option value="">Select Branch</option>
                  {branchtype.map(b => (
                    <option key={b.branch_id} value={String(b.branch_id)}>{b.branch_name}</option>
                  ))}
                </select>
                <span style={{ color: "#6B7280", fontSize: "13px", whiteSpace: "nowrap" }}>
                  {moment().format("ddd, DD MMM YYYY")}
                </span>
              </div>

              {/* Camera */}
              <div className="fk-camera-box">
                <video ref={videoRef} className="fk-video" playsInline muted />
                {(scanning || autoScan) && cameraActive && (
                  <div className="fk-scan-line" />
                )}
                <canvas ref={canvasRef} style={{ display: "none" }} />

                {!cameraActive && (
                  <div style={{
                    position: "absolute", inset: 0, display: "flex", alignItems: "center",
                    justifyContent: "center", flexDirection: "column", gap: "12px", color: "#aaa",
                  }}>
                    <span style={{ fontSize: "48px" }}>📷</span>
                    <span style={{ fontSize: "14px" }}>Camera is off</span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="fk-status">
                {!cameraActive
                  ? "Camera off — click Start Camera"
                  : autoScan
                    ? `🔄 Auto scanning… ${scanning ? "(processing)" : "(waiting for face)"}`
                    : "⏸ Auto scan paused — click Resume or Scan Now"}
              </div>

              {/* Buttons */}
              <div className="fk-btn-row">
                {!cameraActive ? (
                  <button className="btn btn-primary" onClick={startCamera}>Start Camera</button>
                ) : (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={() => handleIdentify(null, false)}
                      disabled={scanning || !selectedBranch}
                    >
                      {scanning && !autoScan ? "Scanning…" : "Scan Now"}
                    </button>
                    <button
                      className={`btn ${autoScan ? "btn-warning" : "btn-outline-primary"}`}
                      onClick={() => setAutoScan(v => !v)}
                      disabled={!selectedBranch}
                    >
                      {autoScan ? "⏸ Pause" : "▶ Resume"}
                    </button>
                    <button className="btn btn-secondary" onClick={stopCamera} disabled={scanning && !autoScan}>
                      Stop Camera
                    </button>
                  </>
                )}
              </div>

              {/* Last punch — auto-clears after 5s */}
              {lastPunch && (
                <div className={`fk-last-punch ${lastPunch.punchType === "out" ? "out-mode" : "in-mode"}`}>
                  <div className="fk-last-label">Last punch</div>
                  <div className="fk-last-name">{lastPunch.name}</div>
                  <div className="fk-last-badge">
                    {lastPunch.punchType === "in" ? "⬆ Punched IN" : "⬇ Punched OUT"} at {moment().format("hh:mm A")}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FaceKiosk;
